import React, { useState, useEffect } from 'react';
import { 
  UsersIcon, 
  UserGroupIcon, 
  BuildingOfficeIcon,
  ClockIcon,
  ArrowTrendingUpIcon,
  ArrowTrendingDownIcon,
  ExclamationTriangleIcon
} from '@heroicons/react/24/outline';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell } from 'recharts';
import { adminManagementAPI } from '../services/api';
import { DashboardStats } from '../types';
import { toast } from 'react-toastify';

const AdminDashboard: React.FC = () => {
  const [stats, setStats] = useState<DashboardStats>({
    totalUsers: 0,
    totalCandidates: 0,
    totalEmployers: 0,
    immediatelyAvailable: 0,
    activeUsers: 0,
    inactiveUsers: 0,
    newUsersThisMonth: 0,
    growthRate: 0,
    trends: {
      userGrowth: 0,
      candidateGrowth: 0,
      employerGrowth: 0,
      availabilityGrowth: 0
    },
    periods: {
      userPeriod: 'this month',
      candidatePeriod: 'this month',
      employerPeriod: 'this month',
      availabilityPeriod: 'this week'
    }
  });
  const [loading, setLoading] = useState(true);

  const COLORS = ['#0ea5e9', '#10b981', '#f59e0b', '#ef4444'];

  const userTypeData = [
    { name: 'Candidates', value: stats.totalCandidates, color: '#0ea5e9' },
    { name: 'Employers', value: stats.totalEmployers, color: '#10b981' },
  ];

  const availabilityData = [
    { name: 'Available Now', value: stats.immediatelyAvailable, color: '#10b981' },
    { name: 'Not Available', value: stats.totalCandidates - stats.immediatelyAvailable, color: '#6b7280' },
  ];

  useEffect(() => {
    loadDashboardStats();
  }, []);

  const loadDashboardStats = async () => {
    try {
      const response = await adminManagementAPI.getDashboard();
      setStats(response.data.stats);
    } catch (error) {
      toast.error('Failed to load dashboard statistics');
    } finally {
      setLoading(false);
    }
  };

  const StatCard: React.FC<{
    title: string;
    value: number;
    icon: React.ComponentType<any>;
    color: string;
    change?: number;
    changeLabel?: string;
  }> = ({ title, value, icon: Icon, color, change, changeLabel }) => (
    <div className="card-admin p-6">
      <div className="flex items-center justify-between">
        <div>
          <p className="text-sm font-medium text-gray-600 dark:text-gray-400">{title}</p>
          <p className="text-3xl font-bold text-gray-900 dark:text-white">{value.toLocaleString()}</p>
          {change !== undefined && (
            <div className="flex items-center mt-2">
              {change >= 0 ? (
                <ArrowTrendingUpIcon className="w-4 h-4 text-green-500 mr-1" />
              ) : (
                <ArrowTrendingDownIcon className="w-4 h-4 text-red-500 mr-1" />
              )}
              <span className={`text-sm font-medium ${change >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                {Math.abs(change)}% {changeLabel}
              </span>
            </div>
          )}
        </div>
        <div className={`p-3 rounded-lg ${color}`}>
          <Icon className="w-6 h-6 text-white" />
        </div>
      </div>
    </div>
  );

  if (loading) {
    return (
      <div className="flex justify-center py-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-admin-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-8">
      <div>
        <h1 className="text-3xl font-bold text-gray-900 dark:text-white">Dashboard Overview</h1>
        <p className="mt-2 text-gray-600 dark:text-gray-300">
          Monitor platform activity and user engagement
        </p>
      </div>

      {/* Key Statistics */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <StatCard
          title="Total Users"
          value={stats.totalUsers}
          icon={UsersIcon}
          color="bg-admin-600"
          change={stats.trends.userGrowth}
          changeLabel={stats.periods.userPeriod}
        />
        <StatCard
          title="Total Candidates"
          value={stats.totalCandidates}
          icon={UserGroupIcon}
          color="bg-blue-600"
          change={stats.trends.candidateGrowth}
          changeLabel={stats.periods.candidatePeriod}
        />
        <StatCard
          title="Total Employers"
          value={stats.totalEmployers}
          icon={BuildingOfficeIcon}
          color="bg-green-600"
          change={stats.trends.employerGrowth}
          changeLabel={stats.periods.employerPeriod}
        />
        <StatCard
          title="Available Immediately"
          value={stats.immediatelyAvailable}
          icon={ClockIcon}
          color="bg-orange-600"
          change={stats.trends.availabilityGrowth}
          changeLabel={stats.periods.availabilityPeriod}
        />
      </div>

      {/* Charts Section */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* User Distribution */}
        <div className="card-admin p-6">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
            User Distribution
          </h3>
          <ResponsiveContainer width="100%" height={300}>
            <PieChart>
              <Pie
                data={userTypeData}
                cx="50%"
                cy="50%"
                outerRadius={80}
                dataKey="value"
                label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
              >
                {userTypeData.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={entry.color} />
                ))}
              </Pie>
              <Tooltip />
            </PieChart>
          </ResponsiveContainer>
        </div>

        {/* Availability Status */}
        <div className="card-admin p-6">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
            Candidate Availability
          </h3>
          <ResponsiveContainer width="100%" height={300}>
            <PieChart>
              <Pie
                data={availabilityData}
                cx="50%"
                cy="50%"
                outerRadius={80}
                dataKey="value"
                label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
              >
                {availabilityData.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={entry.color} />
                ))}
              </Pie>
              <Tooltip />
            </PieChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Quick Actions */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <div className="card-admin p-6">
          <div className="flex items-center space-x-3">
            <div className="p-2 bg-blue-100 dark:bg-blue-900 rounded-lg">
              <UsersIcon className="w-6 h-6 text-blue-600 dark:text-blue-400" />
            </div>
            <div>
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white">User Management</h3>
              <p className="text-sm text-gray-600 dark:text-gray-300">Manage user accounts and permissions</p>
            </div>
          </div>
          <button className="mt-4 w-full btn-admin-secondary">
            Manage Users
          </button>
        </div>

        <div className="card-admin p-6">
          <div className="flex items-center space-x-3">
            <div className="p-2 bg-green-100 dark:bg-green-900 rounded-lg">
              <UserGroupIcon className="w-6 h-6 text-green-600 dark:text-green-400" />
            </div>
            <div>
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Candidate Reviews</h3>
              <p className="text-sm text-gray-600 dark:text-gray-300">Review and moderate candidate profiles</p>
            </div>
          </div>
          <button className="mt-4 w-full btn-admin-secondary">
            Review Candidates
          </button>
        </div>

        <div className="card-admin p-6">
          <div className="flex items-center space-x-3">
            <div className="p-2 bg-amber-100 dark:bg-amber-900 rounded-lg">
              <ExclamationTriangleIcon className="w-6 h-6 text-amber-600 dark:text-amber-400" />
            </div>
            <div>
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white">System Health</h3>
              <p className="text-sm text-gray-600 dark:text-gray-300">Monitor system performance and alerts</p>
            </div>
          </div>
          <button className="mt-4 w-full btn-admin-secondary">
            View System Status
          </button>
        </div>
      </div>

      {/* Recent Activity */}
      <div className="card-admin p-6">
        <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
          Recent Activity
        </h3>
        <div className="space-y-4">
          <div className="flex items-center space-x-3 p-3 bg-gray-50 dark:bg-gray-700 rounded-lg">
            <div className="w-2 h-2 bg-green-500 rounded-full"></div>
            <span className="text-sm text-gray-900 dark:text-white">New candidate registered: John Doe</span>
            <span className="text-xs text-gray-500 dark:text-gray-400 ml-auto">2 minutes ago</span>
          </div>
          <div className="flex items-center space-x-3 p-3 bg-gray-50 dark:bg-gray-700 rounded-lg">
            <div className="w-2 h-2 bg-blue-500 rounded-full"></div>
            <span className="text-sm text-gray-900 dark:text-white">Employer updated company profile: TechCorp</span>
            <span className="text-xs text-gray-500 dark:text-gray-400 ml-auto">15 minutes ago</span>
          </div>
          <div className="flex items-center space-x-3 p-3 bg-gray-50 dark:bg-gray-700 rounded-lg">
            <div className="w-2 h-2 bg-orange-500 rounded-full"></div>
            <span className="text-sm text-gray-900 dark:text-white">Candidate marked as immediately available</span>
            <span className="text-xs text-gray-500 dark:text-gray-400 ml-auto">1 hour ago</span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminDashboard;